/** Auth store — syncs Firebase auth state with Zustand. */

import { create } from "zustand";
import { auth, onAuthStateChanged, type FirebaseUser } from "@/firebase";
import { api } from "@/api/client";
import type { User } from "@/types";

interface AuthState {
  firebaseUser: FirebaseUser | null;
  user: User | null;
  loading: boolean;
  syncing: boolean;
  syncFailed: boolean;
  init: () => void;
  syncUser: () => Promise<void>;
  setUser: (user: User) => void;
}

let initialized = false;
let sessionEpoch = 0;

export const useAuthStore = create<AuthState>((set, get) => ({
  firebaseUser: null,
  user: null,
  loading: true,
  syncing: false,
  syncFailed: false,

  init: () => {
    if (initialized) return;
    initialized = true;

    onAuthStateChanged(auth, (fbUser) => {
      const changed = get().firebaseUser?.uid !== fbUser?.uid;
      if (changed) sessionEpoch += 1;
      set({
        firebaseUser: fbUser,
        loading: false,
        syncFailed: false,
        ...(changed || !fbUser ? { user: null, syncing: false } : {}),
      });
      // Start the new principal's sync before RequireAuth can redirect to login.
      if (fbUser) void get().syncUser();
    });
  },

  setUser: (user: User) => set({ user }),

  syncUser: async () => {
    const { firebaseUser, user, syncing } = get();
    // Already synced, already syncing, or no Firebase session — skip
    if (user || syncing || !firebaseUser) return;

    const epoch = sessionEpoch;
    const uid = firebaseUser.uid;
    const isCurrent = () =>
      sessionEpoch === epoch && get().firebaseUser?.uid === uid && auth.currentUser?.uid === uid;
    set({ syncing: true, syncFailed: false });
    try {
      const backendUser = await api.get<User>("/api/auth/me");
      if (isCurrent()) set({ user: backendUser, syncing: false, syncFailed: false });
    } catch {
      // A superseded sync must not change the current principal's state.
      if (isCurrent()) set({ user: null, syncing: false, syncFailed: true });
    }
  },
}));
